/**
 * Which in-app browser (if any) the page is running inside.
 *
 * Why this exists, measured 2026-09-19: the one-time («Оплатить разово») hosted
 * checkout leaves the SPA with `window.location.assign(redirectUrl)` to
 * Payme/Click/OCTO. Inside the INSTAGRAM in-app browser on ANDROID that is a
 * dead end — the WebView cannot complete the bank's 3-DS step and cannot hand
 * off to the Click app, so 0 of 16 one-time orders opened there since 06.09 ever
 * completed, against 25 of 59 on desktop. The CARD-subscription flow in the same
 * browser does work (3 of 7 completed) and is deliberately untouched.
 *
 * Scope was Instagram only at first: Facebook and Telegram in-app browsers had
 * produced no one-time orders in that window. Telegram joined 2026-09-27 and
 * Facebook 2026-10-03 — see the notes on each token below.
 */

/**
 * The in-app browsers we recognise. `null` = a normal browser, or one we do not
 * treat specially.
 *
 * `telegram` since 2026-09-27 (then for «Оплата по телефону», since 01.10 for
 * `cardOnlyCheckout`). The external hand-off below stays Instagram-only: nothing
 * was measured about Telegram's WebView and `intent://`, and this is not the
 * place to guess.
 */
export type InAppApp = 'instagram' | 'facebook' | 'telegram' | null;
export type InAppOs = 'android' | 'ios' | 'other';

export interface InAppBrowser {
  app: InAppApp;
  os: InAppOs;
}

/**
 * Instagram appends its own token to an otherwise ordinary WebView UA:
 *
 *   Android: … ; wv) AppleWebKit/537.36 … Mobile Safari/537.36 Instagram 4xx…
 *   iPhone:  … Mobile/23G90 Instagram 446.0.0.28.66 (iPhone…
 *
 * The `wv)` marker alone is NOT enough — every Android WebView carries it,
 * including ones where the redirect works fine — so the app token is what we
 * match on.
 */
const INSTAGRAM = /\bInstagram[\s/]/i;
/**
 * Facebook's in-app browser appends its own bracketed tokens:
 *
 *   Android: … Mobile Safari/537.36 [FB_IAB/FB4A;FBAV/5xx.0.0.xx.xx;IABMV/1;]
 *   iPhone:  … Mobile/22F76 Safari/604.1 [FBAN/FBIOS;FBAV/5xx.0.0.xx.xx;…]
 *
 * ⚠️ On Android it carries NO `; wv)` token (real UAs, 2026-10-03), so
 * `isAndroidWebView()` below stays false for it: the keyboard-lift assumption
 * and the server-download fallback are NOT extended here — nothing was measured
 * about how that browser resizes for a keyboard or hands over a download.
 *
 * Measured 2026-10-03 (`events.session_start`, 30 days): 1 317 Android and 463
 * iOS sessions from it — a tenth of Instagram's — 13 checkouts opened, 1 paid.
 * Until this date it read as an ordinary browser: `inApp` on its events was
 * `false`, the monthly card sold both buttons there, and on 01.10 a Business
 * one-time checkout inside it was sent to the hosted Payme page (PostHog
 * 01a0f85a). The server already matched these tokens
 * (`services/api/src/common/telegram/bot-login-store.ts`).
 */
const FACEBOOK = /\bFBAN\/|\bFBAV\/|\bFB_IAB\//;
/**
 * Telegram's own in-app browser on Android appends `Telegram-Android/<version>
 * (<maker> <model>; Android <n>; SDK <n>; <class>)` to the WebView UA.
 *
 * ⚠️ Measured 2026-09-27: NOT ONE `session_start` in 30 days carried it (40k
 * sessions, 5.3k of them Instagram). Telegram on Android opens most links in a
 * Chrome Custom Tab — a real Chrome, where the hosted page works — so this
 * matches only the app's own WebView when it is used, and is expected to be
 * rare. It is here because a Telegram WebView, when it does show up, has
 * exactly the dead-end the Instagram one has.
 */
const TELEGRAM_ANDROID = /\bTelegram-Android\//;
const ANDROID = /\bAndroid\b/i;
const IOS = /\b(iPhone|iPad|iPod)\b/i;

export function detectInAppBrowser(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): InAppBrowser {
  const os: InAppOs = IOS.test(ua) ? 'ios' : ANDROID.test(ua) ? 'android' : 'other';
  const app: InAppApp = INSTAGRAM.test(ua)
    ? 'instagram'
    : FACEBOOK.test(ua)
      ? 'facebook'
      : TELEGRAM_ANDROID.test(ua)
        ? 'telegram'
        : null;
  return { app, os };
}

/**
 * True when this environment is the one the external hand-off is for: the
 * Meta in-app browsers on Android. Facebook since 2026-10-03: same family, same
 * reason to leave the host app before a bank page.
 */
export function needsExternalHandoff(inApp: InAppBrowser): boolean {
  return (inApp.app === 'instagram' || inApp.app === 'facebook') && inApp.os === 'android';
}

/**
 * CARD ONLY (2026-10-01): the monthly plan card sells the card subscription
 * alone — one button, «Оплатить картой» — with no one-time option.
 *
 * Instagram, Telegram or (since 2026-10-03) Facebook, on ANDROID only. The WebViews there dead-end the
 * hosted one-time checkout: 19–27.09 37 people opened it there, 14 reached the
 * Payme/Click page and 1 paid; the SMS invoice that replaced it (27.09) got 0 of
 * 11; our own card sheet took 4 of the 5 purchases from this browser since
 * 19.09. iOS is deliberately left alone — nothing was measured there.
 */
export function cardOnlyCheckout(inApp: InAppBrowser): boolean {
  return inApp.app !== null && inApp.os === 'android';
}

/**
 * An ANDROID WEBVIEW — any host app's, not only Instagram's.
 *
 * ⚠️ Read the note on `INSTAGRAM` above before reusing this for the checkout
 * hand-off: there, `wv)` is deliberately NOT enough, because plenty of WebViews
 * complete a redirect perfectly well and special-casing them would be a guess.
 * Here it is exactly the right scope, because the thing that breaks is the
 * WebView itself: downloads are routed through the host app's
 * `DownloadListener`, which is only ever offered `http(s)`/`data` URLs. A
 * `blob:` URL is never handed over and `<a download>` is ignored — in EVERY
 * Android WebView, whoever embeds it.
 *
 * The `; wv)` token is the platform's own marker for "embedded WebView, not
 * Chrome", and Chrome itself never carries it.
 */
const ANDROID_WEBVIEW = /;\s*wv[);]/i;

export function isAndroidWebView(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): boolean {
  return ANDROID.test(ua) && ANDROID_WEBVIEW.test(ua);
}

/**
 * Must a download come from the SERVER as a normal `https` response, because
 * handing this browser a blob would do nothing?
 *
 * Two populations, one remedy:
 *   - Android WebViews — see above. The `download` attribute is present (they
 *     are Chromium), so `save-blob.ts`'s "no `download` support" fallback never
 *     fires and the click is a silent no-op. This is the case that cost
 *     `samursamar708@…` 18 taps and three rage-clicks on 2026-09-21.
 *   - Browsers with no `download` attribute at all (iOS in-app WebViews).
 *     `saveBlob` opens the blob in a tab there, which SHOWS the file but never
 *     saves it; a real attachment URL does both.
 *
 * `hasDownloadAttribute` is a parameter so the predicate stays testable without
 * touching a prototype the whole suite shares.
 */
export function needsServerDownload(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  hasDownloadAttribute: boolean = typeof HTMLAnchorElement !== 'undefined' &&
    'download' in HTMLAnchorElement.prototype,
): boolean {
  return isAndroidWebView(ua) || !hasDownloadAttribute;
}

/**
 * Analytics context for the checkout events — `{}` in an ordinary browser, so
 * the existing rows keep exactly the shape they had.
 */
export function inAppContext(
  inApp: InAppBrowser = detectInAppBrowser(),
): { inApp: Exclude<InAppApp, null>; inAppOs: InAppOs } | Record<string, never> {
  return inApp.app ? { inApp: inApp.app, inAppOs: inApp.os } : {};
}

/**
 * The Android intent URL that opens `url` in the phone's own browser instead of
 * this WebView.
 *
 * ⚠️ NOT pinned to Chrome (since 2026-09-23). The first cut carried
 * `package=com.android.chrome`, and the fallback promised that a phone without
 * Chrome would open the original URL "in whatever the default handler is". It
 * does — INSIDE THE WEBVIEW. Measured 22–23.09: of five hand-offs from
 * Instagram/Android, three were swallowed, all on Xiaomi phones (M2006C3LG on
 * Android 10, 2412DPC0AG on Android 16, MIUI ships Mi Browser as the default
 * and Chrome is often absent or disabled), and on every one of them Payme's
 * page then loaded in the WebView itself and the payment died there — 0 paid.
 * The audience is overwhelmingly Redmi/Xiaomi on Android 10 (session UAs,
 * 22–23.09). Without the pin Android resolves the intent against every
 * browser on the phone — Mi Browser, Samsung Internet, Chrome — and against
 * an app that owns the host through App Links, which for a payment page is
 * better still. `category=BROWSABLE` says this came from a web page, which is
 * what a browser's intent filter requires.
 *
 * `S.browser_fallback_url` stays as the safety net for a phone with no
 * browser at all (nothing resolves → the original https URL loads here,
 * rather than nothing happening).
 *
 * ⚠️ A fragment cannot survive the syntax — `#Intent;…` IS the fragment — so the
 * intent target carries host+path+query only. The full original URL, fragment
 * included, is preserved in the fallback. No provider redirect we issue uses a
 * fragment; if one ever did, the fallback path is still exact.
 *
 * Returns `null` for anything that is not an absolute https URL, so the caller
 * keeps its ordinary navigation rather than guessing at an intent we cannot
 * build.
 */
export function androidIntentUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  const target = `${parsed.host}${parsed.pathname}${parsed.search}`;
  return (
    `intent://${target}#Intent;scheme=https;` +
    `action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;` +
    `S.browser_fallback_url=${encodeURIComponent(url)};end`
  );
}

/**
 * Hand `url` to the phone's real browser. Returns false when that was not
 * possible, and the caller must then navigate normally — a checkout must never
 * end in "nothing happened".
 */
export function openExternally(url: string): boolean {
  const intentUrl = androidIntentUrl(url);
  if (!intentUrl || typeof window === 'undefined') return false;
  window.location.href = intentUrl;
  return true;
}

/**
 * How long to wait for Android to act on the intent before calling it swallowed.
 *
 * 2500 ms. An intent that resolves backgrounds this page within a few hundred
 * milliseconds — the app-switch animation is the slow part, not the resolution —
 * so the deadline is several times the expected signal. It is also short enough
 * that a buyer looking at a sheet which did nothing is not left looking for long.
 */
export const HANDOFF_DEADLINE_MS = 2500;

/**
 * The same deadline inside INSTAGRAM's in-app browser — for the Telegram bot
 * login's hand-off only (2026-10-01).
 *
 * Instagram answers a link into another app with its OWN native dialog first,
 * «Вы покидаете приложение?», and while that dialog is up the page stays
 * visible AND focused: no `visibilitychange`, no `blur`, nothing
 * `watchHandoff` can see. So at 2.5 s the watcher declared the hand-off
 * swallowed while the person was still reading the dialog. Verified on a
 * Redmi 15C: after «Продолжить» Telegram opens and the login goes through —
 * the «не открылся» line and its `intent_ignored` row were both false, and a
 * real Instagram/Android visitor got exactly that on prod (30.09 05:40).
 *
 * 12 s is a budget for reading one dialog and tapping it, not a resolution
 * time. ⚠️ The checkout's `intent://` hand-off leaves through the same app and
 * may meet the same dialog, but nothing was measured there — it keeps
 * `HANDOFF_DEADLINE_MS`.
 *
 * ⚠️ 45 s since 2026-10-17, and FACEBOOK's in-app browser too (the name is
 * kept so nothing else moves). 12 s was still short: on a Redmi in Instagram
 * on 16.10 the dialog stayed up past it, `intent_ignored` was written, and
 * Telegram opened 33 s after the tap. Measured 02–09.10 on logins that DID go
 * through: 8 % of Instagram/Android ones kept the page visible longer than
 * 12 s, 17 % of Facebook/Android ones longer than 2.5 s. The watcher writes
 * nothing once the page has gone away, so a longer window removes the rows
 * written while a dialog was still being read — and also the row of someone
 * whose link really was swallowed and who left, or tapped again, inside 45 s.
 * The series stays a floor, now a lower one; the «не открылся» line waits as
 * long, with «Открыть Telegram ещё раз» on screen the whole time.
 *
 * ⚠️ Since 2026-10-18 this bounds the FUNNEL ROW only. On /login and
 * /register the on-screen «Не открылся Telegram? …» with «Скопировать ссылку»
 * comes after ~2 s (`IN_APP_HINT_MS`, owner): a hint that is harmless while the
 * dialog is up, not a verdict. (The Android in-app hand-off itself is still
 * the pinned intent; elsewhere it is `https://t.me`.)
 */
export const INSTAGRAM_HANDOFF_DEADLINE_MS = 45_000;

/**
 * Did the Android intent actually take?
 *
 * ## The hole this closes
 *
 * `openExternally` sets `window.location.href = 'intent://…'` and returns. If
 * Android resolves it, this page goes to background; if the WebView swallows the
 * scheme, **nothing happens at all** — the sheet stays exactly as it was and the
 * buyer waits for a page that will never come. We emitted nothing after that
 * navigation, so the two outcomes were indistinguishable: an order row, a
 * `pay_once_opened{handoff:'external'}`, then silence.
 *
 * Measured 2026-09-19: 20 hosted orders from Instagram/Android in two weeks, 8
 * reached the Payme page, 0 paid. For the one traced by hand (order 606, Samsung
 * SM-A135F, Android 14) the Payme page was never reached — and nothing in the
 * data could tell "intent ignored" from "opened and closed instantly".
 *
 * ## What counts as "it took"
 *
 * Any sign the page went away: `visibilitychange` to hidden, `pagehide`, or a
 * `window` blur. Each cancels the watcher, and nothing is emitted.
 *
 * ⚠️ A bare `blur` counts as "it took" even if `visibilityState` never turns
 * hidden — more forgiving than "blur followed by hidden", deliberately and in
 * one direction only. The worst a false alarm can do is tell a buyer their
 * payment page did not open when it did, and poison the very ratio this exists
 * to measure; a missed detection only costs a row in a counter. WebView blur and
 * visibility behaviour is not uniform across Android OEM shells, so the
 * conservative reading is the right default until a measurement says otherwise.
 *
 * @param onIgnored called at most ONCE, and only if the page never went away.
 * @returns a cancel function — call it on unmount, on sheet close, and before
 *          arming a second attempt.
 */
export function watchHandoff(
  onIgnored: () => void,
  deadlineMs: number = HANDOFF_DEADLINE_MS,
): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => undefined;

  let settled = false;
  let timer = 0;

  function stop(): void {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onGone);
    window.removeEventListener('blur', onGone);
  }
  function onVisibility(): void {
    // `visibilitychange` fires on the way BACK as well; only the hidden
    // direction means the page left.
    if (document.visibilityState === 'hidden') stop();
  }
  function onGone(): void {
    stop();
  }

  timer = window.setTimeout(() => {
    if (settled) return;
    // One last look before accusing the WebView: a page that is already hidden
    // at the deadline went away without telling us.
    const hidden = document.visibilityState === 'hidden';
    stop();
    if (!hidden) onIgnored();
  }, deadlineMs);

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onGone);
  window.addEventListener('blur', onGone);
  return stop;
}
